import React, { useState } from 'react';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FileUploadStep } from '../components/leads/import/FileUploadStep';
import { FieldMappingStep } from '../components/leads/import/FieldMappingStep';
import { ValidationStep } from '../components/leads/import/ValidationStep';
import { ReviewStep } from '../components/leads/import/ReviewStep';
import { ImportProgressStep } from '../components/leads/import/ImportProgressStep';
import { ImportResultsStep } from '../components/leads/import/ImportResultsStep';
import {
  UploadResponse,
  ValidationSummary,
  FieldMapping,
  ImportOptions,
} from '../types/import.types';

const steps = [
  'Upload File',
  'Map Fields',
  'Validate',
  'Review',
  'Import',
  'Complete',
];

export const LeadImportPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [uploadData, setUploadData] = useState<UploadResponse | null>(null);
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({});
  const [validationData, setValidationData] = useState<ValidationSummary | null>(null);
  const [importOptions, setImportOptions] = useState<ImportOptions>({
    defaultStatus: 'new',
    defaultPriority: 'medium',
    assignToCreator: true,
    duplicateHandling: 'skip',
  });

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleUploadComplete = (data: UploadResponse) => {
    setUploadData(data);
    handleNext();
  };

  const handleMappingComplete = (mapping: FieldMapping) => {
    setFieldMapping(mapping);
    handleNext();
  };

  const handleValidationComplete = (data: ValidationSummary) => {
    setValidationData(data);
    handleNext();
  };

  const handleReviewComplete = () => {
    handleNext();
  };

  const handleImportComplete = () => {
    handleNext();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <FileUploadStep onComplete={handleUploadComplete} />;
      
      case 1:
        return uploadData ? (
          <FieldMappingStep
            headers={uploadData.headers}
            preview={uploadData.preview}
            onComplete={handleMappingComplete}
            onBack={handleBack}
          />
        ) : null;
      
      case 2:
        return uploadData ? (
          <ValidationStep
            jobId={uploadData.jobId}
            fieldMapping={fieldMapping}
            options={importOptions}
            onComplete={handleValidationComplete}
            onBack={handleBack}
          />
        ) : null;
      
      case 3:
        return validationData ? (
          <ReviewStep
            jobId={validationData.jobId}
            summary={validationData.summary}
            onComplete={handleReviewComplete}
            onBack={handleBack}
            onOptionsChange={setImportOptions}
            options={importOptions}
          />
        ) : null;
      
      case 4:
        return validationData ? (
          <ImportProgressStep
            jobId={validationData.jobId}
            options={importOptions}
            onComplete={handleImportComplete}
          />
        ) : null;
      
      case 5:
        return validationData ? (
          <ImportResultsStep
            jobId={validationData.jobId}
            onFinish={() => navigate('/leads')}
          />
        ) : null;
      
      default:
        return null;
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/leads')}
            sx={{ mr: 2 }}
          >
            Back to Leads
          </Button>
          <Typography variant="h5">
            Import Leads
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {renderStepContent()}
    </Box>
  );
};